import React from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AnDatePicker from '../../components/DateTime/DatePicker'
import { FormSection } from './FormKit'
import { ADDRESS_HISTORY_YEARS, addressCoverage } from './applicationHistory'

const ADDRESS_FIELDS = [
  { field: 'lineOne', label: 'Address line 1', autoComplete: 'address-line1', required: true },
  { field: 'lineTwo', label: 'Address line 2', autoComplete: 'address-line2' },
  { field: 'town', label: 'Town', autoComplete: 'address-level2' },
  { field: 'city', label: 'City', autoComplete: 'address-level2' },
  { field: 'county', label: 'County', autoComplete: 'address-level1' },
  { field: 'postCode', label: 'Postcode', autoComplete: 'postal-code', required: true },
]

const AddressFields = ({ value, onChange, idPrefix }) => (
  <Grid container spacing={2}>
    {ADDRESS_FIELDS.map(({ field, label, autoComplete, required }) => (
      <Grid item xs={12} sm={6} key={field}>
        <TextField
          id={`${idPrefix}-${field}`}
          label={label}
          size="small"
          fullWidth
          required={required}
          autoComplete={autoComplete}
          value={value?.[field] ?? ''}
          onChange={(e) =>
            onChange({
              ...value,
              [field]:
                field === 'postCode'
                  ? e.target.value.toUpperCase()
                  : e.target.value,
            })
          }
        />
      </Grid>
    ))}
  </Grid>
)

// Monthly precision is all a DBS address history needs
const monthPickerProps = {
  views: ['year', 'month'],
  openTo: 'year',
  format: 'MMM YYYY',
  disableFuture: true,
  size: 'small',
  fullWidth: true,
}

const AddressHistory = ({ formValues, setFormValues }) => {
  const currentAddress = formValues?.employee?.personnelInfo?.address
  const history = formValues?.addressHistory ?? []
  const { needsHistory, isCovered, coveredFrom } = addressCoverage(
    formValues?.currentAddressSince,
    history,
  )

  const setCurrentAddress = (address) =>
    setFormValues((prev) => ({
      ...prev,
      employee: {
        ...prev.employee,
        personnelInfo: { ...prev.employee?.personnelInfo, address },
      },
    }))

  const setHistory = (updater) =>
    setFormValues((prev) => ({
      ...prev,
      addressHistory: updater(prev.addressHistory ?? []),
    }))

  const updatePrevious = (index, patch) =>
    setHistory((list) =>
      list.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)),
    )

  // Each new previous address naturally ends where the earliest one so far
  // begins, so suggest that as its "to" date.
  const addPrevious = () =>
    setHistory((list) => [...list, { to: coveredFrom?.toISOString() ?? null }])

  return (
    <Stack gap={2}>
      <FormSection title="Current address">
        <AddressFields
          idPrefix="address"
          value={currentAddress}
          onChange={setCurrentAddress}
        />
        <Grid container>
          <Grid item xs={12} sm={6}>
            <AnDatePicker
              label="Lived here since"
              required
              date={formValues?.currentAddressSince ?? null}
              onChange={(date) =>
                setFormValues((prev) => ({
                  ...prev,
                  currentAddressSince: date,
                }))
              }
              {...monthPickerProps}
            />
          </Grid>
        </Grid>
      </FormSection>

      {(needsHistory || history.length > 0) && (
        <FormSection
          title="Previous addresses"
          caption={`Add every address you have lived at in the last ${ADDRESS_HISTORY_YEARS} years, most recent first.`}
        >
          {needsHistory && (
            <Alert severity={isCovered ? 'success' : 'info'}>
              {isCovered
                ? `Thanks — your addresses cover the last ${ADDRESS_HISTORY_YEARS} years.`
                : `Your addresses currently go back to ${coveredFrom.format(
                    'MMMM YYYY',
                  )}. Keep adding previous addresses until you reach ${moment()
                    .subtract(ADDRESS_HISTORY_YEARS, 'years')
                    .format('MMMM YYYY')}.`}
            </Alert>
          )}
          {history.map((entry, index) => (
            <Stack
              key={entry._id ?? index}
              gap={2}
              sx={{
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Stack direction="row" gap={2} alignItems="flex-start">
                <Grid container spacing={2} flex={1}>
                  <Grid item xs={12} sm={6}>
                    <AnDatePicker
                      label="From"
                      required
                      date={entry.from ?? null}
                      onChange={(date) => updatePrevious(index, { from: date })}
                      {...monthPickerProps}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <AnDatePicker
                      label="To"
                      date={entry.to ?? null}
                      onChange={(date) => updatePrevious(index, { to: date })}
                      {...monthPickerProps}
                    />
                  </Grid>
                </Grid>
                <Tooltip title="Remove this address">
                  <IconButton
                    onClick={() =>
                      setHistory((list) => list.filter((_, i) => i !== index))
                    }
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <AddressFields
                idPrefix={`previous-address-${index}`}
                value={entry}
                onChange={(address) => updatePrevious(index, address)}
              />
            </Stack>
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addPrevious}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add a previous address
          </Button>
        </FormSection>
      )}
    </Stack>
  )
}

export default AddressHistory
